import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import {
  addMultipleAddonToOrderItem,
  deleteNonBookableOrderItem,
  getPerParticipantFieldsOfOrder,
  updateAddonOfOrderItem,
  updateOrderCustomer,
  updatePerBookingFieldsOfOrder,
  updatePerParticipantFieldsOfOrder,
  updatePriceOfOrderItem,
  updateQuantityOfOrderItem,
} from "../../services/OrderServices"
import {
  GetPerOrderFieldsResponseType,
  UpdatePerBookingFieldsOfOrderRequestType,
} from "../../types/CartTypes"
import {
  AddMultipleAddonToOrderItemRequestType,
  GetOrderDetailsResponseType,
  GetPerParticipantFieldsOfOrderResponseType,
  UpdateAddonOfOrderItemRequestType,
  UpdateOrderCustomerRequestType,
  UpdatePerParticipantFieldsOfOrderRequestType,
  UpdatePriceOfOrderItemRequestType,
  UpdateQuantityOfOrderItemRequestType,
} from "../../types/OrderTypes"
import QueryKeys from "../../utils/QueryKeys"
import { GenericMutateOptionsType, GenericQueryOptionsType } from "../cart/hooks"

import { cancelGiftCardRefund, updateProductOfOrderItem, getOrderParticipants } from "./services"
import {
  GetOrderParticipantsResponseType,
  ICancelGiftCardRefundRequestType,
  ICancelGiftCardRefundResponseType,
} from "./types"

export const useCancelGiftCardRefundMutation = () => {
  return useMutation<
    ICancelGiftCardRefundResponseType,
    AxiosError<Record<string, string>>,
    ICancelGiftCardRefundRequestType
  >({
    mutationFn: async ({ orderId, transactionId, reason }) => {
      const { data } = await cancelGiftCardRefund({ orderId, transactionId, reason })
      return data
    },
  })
}

export interface UseGetParticipantsFromCartProps
  extends GenericQueryOptionsType<GetPerParticipantFieldsOfOrderResponseType> {
  orderId: string
  isIntegratedPage?: boolean
}
export const useGetAllParticipantsOfOrder = ({
  orderId,
  isIntegratedPage,
  queryOptions,
}: UseGetParticipantsFromCartProps) =>
  useQuery({
    enabled: Boolean(orderId),
    ...queryOptions,
    queryKey: [QueryKeys.order.orderParticipantsFields, orderId, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getPerParticipantFieldsOfOrder(orderId, isIntegratedPage)
      return data
    },
  })

export const useUpdateProductOfOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: UpdateQuantityOfOrderItemRequestType
    orderId: string
    orderItemId: string
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, payload }) => {
      const { data } = await updateProductOfOrderItem(orderId, orderItemId, payload)
      return data
    },
    ...mutateOptions,
  })
export interface UseGetParticipantsOfOrderProps
  extends GenericQueryOptionsType<GetOrderParticipantsResponseType> {
  orderId: string
  isIntegratedPage?: boolean
}
export const useGetOrderParticipants = ({
  orderId,
  isIntegratedPage,
  queryOptions,
}: UseGetParticipantsOfOrderProps) =>
  useQuery({
    ...queryOptions,
    queryKey: [QueryKeys.order.orderParticipants, orderId, isIntegratedPage],
    queryFn: async () => {
      const { data } = await getOrderParticipants(orderId, isIntegratedPage)
      return data
    },
    placeholderData: keepPreviousData,
  })

export const useUpdateQuantityOfOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: UpdateQuantityOfOrderItemRequestType
    orderId: string
    orderItemId: string
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, payload }) => {
      const { data } = await updateQuantityOfOrderItem(orderId, orderItemId, payload)
      return data
    },
    ...mutateOptions,
  })

export const useUpdatePriceOfOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    payload: UpdatePriceOfOrderItemRequestType
    orderId: string
    orderItemId: string
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, payload }) => {
      const { data } = await updatePriceOfOrderItem(orderId, orderItemId, payload)
      return data
    },
    ...mutateOptions,
  })

export const useDeleteNonBookableOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    orderItemId: string
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId }) => {
      const { data } = await deleteNonBookableOrderItem(orderId, orderItemId)
      return data
    },
    ...mutateOptions,
  })

export const useUpdatePerBookingFieldsOfOrderMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    body: UpdatePerBookingFieldsOfOrderRequestType
    isIntegratedPage?: boolean
  },
  GetPerOrderFieldsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, body, isIntegratedPage }) => {
      const { data } = await updatePerBookingFieldsOfOrder(orderId, body, isIntegratedPage)
      return data
    },
    ...mutateOptions,
  })

export const useUpdatePerParticipantFieldsOfOrderMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    body: UpdatePerParticipantFieldsOfOrderRequestType[]
    isIntegratedPage?: boolean
  },
  GetPerParticipantFieldsOfOrderResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, body, isIntegratedPage }) => {
      const { data } = await updatePerParticipantFieldsOfOrder(orderId, body, isIntegratedPage)
      return data
    },
    ...mutateOptions,
  })

export const useAddMultipleAddonToOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    orderItemId: string
    body: AddMultipleAddonToOrderItemRequestType
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, body }) => {
      const { data } = await addMultipleAddonToOrderItem(orderId, orderItemId, body)
      return data
    },
    ...mutateOptions,
  })

export const useUpdateAddonOfOrderItemMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    orderItemId: string
    body: UpdateAddonOfOrderItemRequestType
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, orderItemId, body }) => {
      const { data } = await updateAddonOfOrderItem(orderId, orderItemId, body)
      return data
    },
    ...mutateOptions,
  })

export const useUpdateCustomerInOrderMutation = ({
  mutateOptions,
}: GenericMutateOptionsType<
  {
    orderId: string
    body: UpdateOrderCustomerRequestType
  },
  GetOrderDetailsResponseType
> = {}) =>
  useMutation({
    mutationFn: async ({ orderId, body }) => {
      const { data } = await updateOrderCustomer(orderId, body)
      return data
    },
    ...mutateOptions,
  })
